import type { KnowledgeArticle } from "../types"

export const aiAgentLatenBouwen: KnowledgeArticle = {
  id: "insight:ai-agent-laten-bouwen",
  pageType: "knowledge-article",
  slug: { nl: "ai-agent-laten-bouwen" },
  title: {
    nl: "AI Agent Laten Maken of Bouwen | Wat het kost, wat het kan",
    en: "AI Agent Laten Maken of Bouwen | Wat het kost, wat het kan",
  },
  description: {
    nl: "Een AI-agent laten maken of bouwen: wat kan hij zelfstandig, wat niet — en wat komt er kijken bij een agent die veilig met je bedrijfsprocessen werkt?",
    en: "What is an AI agent, what can it do autonomously, what not — and what does it take to build an agent that safely works with your business processes?",
  },
  h1: {
    nl: "AI agent laten maken of bouwen: zo werkt het",
    en: "AI agent laten maken of bouwen: zo werkt het",
  },
  published: "2026-08-26",
  modified: "2026-10-29",
  author: "Camiel Schmeitz",
  sections: [
    {
      type: "paragraph",
      text: "Steeds meer bedrijven willen een AI-agent laten maken of bouwen, maar de term wordt voor alles en nog wat gebruikt. In dit artikel leggen we uit wat een agent technisch gezien is, wanneer hij waarde toevoegt — en wanneer een gewone automatisering of chatbot de betere keuze is.",
    },
    {
      type: "heading",
      text: "Wat is een AI-agent precies?",
    },
    {
      type: "paragraph",
      text: "Een AI-agent is een systeem dat op basis van een taalmodel niet alleen antwoorden geeft, maar ook acties onderneemt. Het verschil met een gewone chatbot zit in die actie: een agent kan tools aanroepen (API's, databases, bestandssystemen), informatie ophalen uit meerdere bronnen en meerstaps-taken zelfstandig uitvoeren. Denk aan een assistent die eerst begrijpt wat een bezoeker vraagt, vervolgens in een kennisbank zoekt en tot slot automatisch naar de juiste pagina of afdeling doorverwijst — precies het patroon dat we gebruikten voor de navigatieassistent van Burgerkracht.",
    },
    {
      type: "heading",
      text: "Waarom zou ik een agent laten bouwen in plaats van kopen?",
    },
    {
      type: "paragraph",
      text: "Standaard AI-tools zijn goed voor algemene taken. Zodra een agent moet werken met jouw specifieke processen, systemen en taalgebruik, bepaalt maatwerk de kwaliteit. Een gebouwde agent kan gekoppeld worden aan exact jouw kennisbank, mag precies de acties uitvoeren die jij toestaat en volgt de regels van jouw organisatie in plaats van generieke instellingen.",
    },
    {
      type: "heading",
      text: "Wat komt er allemaal bij kijken?",
    },
    {
      type: "list",
      items: [
        "Taakdefinitie en machtiging: welke taken voert de agent zelfstandig uit, en waar keurt een mens goed?",
        "Toegang tot kennis: via retrieval (RAG) antwoordt de agent vanuit jouw documentatie in plaats van uit algemene modelkennis.",
        "Tool-integratie: koppelingen met systemen zoals CRM, ticketing of publicatieplatforms.",
        "Grenzen en logging: elke actie wordt vastgelegd en er zijn harde grenzen aan wat de agent mag doen.",
        "Productiebeheer: monitoring van kwaliteit en kosten, misbruikpreventie en updates wanneer modellen wijzigen.",
      ],
    },
    {
      type: "heading",
      text: "Veelgemaakte fouten",
    },
    {
      type: "list",
      items: [
        "De agent te veel vrijheid geven zonder goedkeurstappen — vertrouwen moet groeien per taak.",
        "Starten zonder heldere taakomschrijving; 'een assistant' is geen specificatie.",
        "Kwaliteit meten op demo's in plaats van op realistische scenario's uit de eigen praktijk.",
        "Vergeten dat kennis actueel gehouden moet worden: een agent met verouderde documentatie geeft stellig verkeerde antwoorden.",
      ],
    },
    {
      type: "heading",
      text: "Wanneer is een agent geen goed idee?",
    },
    {
      type: "paragraph",
      text: "Als het proces altijd dezelfde vaste stappen volgt, is klassieke automatisering goedkoper en betrouwbaarder. Een agent is pas interessant wanneer er variatie in inhoud of situatie zit en interpretatie nodig is. En als er geen toegang tot goede interne kennis of systemen is, blijft elk antwoord oppervlakkig.",
    },
    {
      type: "callout",
      title: "Concreet advies",
      text: "Begin met één afgebakende taak met duidelijke succesmaatstaven. Bewijs daar de waarde, breid daarna uit. Wij adviseren bedrijven regelmatig om juist níet te starten — dat scheelt geld en teleurstelling.",
    },
  ],
  relatedServiceIds: ["ai-agents", "ai-knowledge-systems"],
  relatedCaseIds: ["burgerkracht"],
}
